import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const CreatePlanTab = () => {
  const [formData, setFormData] = useState({
    planName: '',
    planDescription: '',
    monthlyPrice: '',
    yearlyPrice: '',
    trialDays: '',
    maxReportsLimit: '',
    aiUsageLimit: '',
    features: {
      bmcAccess: false,
      predictiveAnalysis: false,
      goalIntelligence: false,
      exportReports: false,
      prioritySupport: false
    },
    teamMemberLimit: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFeatureToggle = (feature) => {
    setFormData({
      ...formData,
      features: {
        ...formData?.features,
        [feature]: !formData?.features?.[feature]
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.planName?.trim()) {
      newErrors.planName = 'Plan name is required';
    }

    if (!formData?.planDescription?.trim()) {
      newErrors.planDescription = 'Plan description is required';
    }

    if (!formData?.monthlyPrice || isNaN(formData?.monthlyPrice) || Number(formData?.monthlyPrice) < 0) {
      newErrors.monthlyPrice = 'Valid monthly price is required';
    }

    if (!formData?.yearlyPrice || isNaN(formData?.yearlyPrice) || Number(formData?.yearlyPrice) < 0) {
      newErrors.yearlyPrice = 'Valid yearly price is required';
    }

    if (!formData?.trialDays || isNaN(formData?.trialDays) || Number(formData?.trialDays) < 0) {
      newErrors.trialDays = 'Valid trial days is required';
    }

    if (!formData?.maxReportsLimit || isNaN(formData?.maxReportsLimit)) {
      newErrors.maxReportsLimit = 'Valid reports limit is required';
    }

    if (!formData?.aiUsageLimit || isNaN(formData?.aiUsageLimit)) {
      newErrors.aiUsageLimit = 'Valid AI usage limit is required';
    }

    if (!formData?.teamMemberLimit || isNaN(formData?.teamMemberLimit) || Number(formData?.teamMemberLimit) < 1) {
      newErrors.teamMemberLimit = 'Valid team member limit is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      console.log('Plan created:', formData);
      setIsSubmitting(false);
      handleCancel();
    }, 1000);
  };

  const handleCancel = () => {
    setFormData({
      planName: '',
      planDescription: '',
      monthlyPrice: '',
      yearlyPrice: '',
      trialDays: '',
      maxReportsLimit: '',
      aiUsageLimit: '',
      features: {
        bmcAccess: false,
        predictiveAnalysis: false,
        goalIntelligence: false,
        exportReports: false,
        prioritySupport: false
      },
      teamMemberLimit: '',
      isActive: true
    });
    setErrors({});
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary" />
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Plan Name"
                type="text"
                value={formData?.planName}
                onChange={(e) => handleInputChange('planName', e?.target?.value)}
                placeholder="e.g., Growth Plan"
                required
                error={errors?.planName}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Plan Description <span className="text-destructive">*</span>
              </label>
              <textarea
                value={formData?.planDescription}
                onChange={(e) => handleInputChange('planDescription', e?.target?.value)}
                placeholder="Describe the plan benefits and target audience..."
                rows={4}
                className={`w-full px-3 py-2 bg-background border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                  errors?.planDescription ? 'border-destructive' : 'border-border'
                }`}
              />
              {errors?.planDescription && (
                <p className="text-sm text-destructive mt-1">{errors?.planDescription}</p>
              )}
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="DollarSign" size={20} className="text-primary" />
            Pricing
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Monthly Price (₹)"
              type="number"
              value={formData?.monthlyPrice}
              onChange={(e) => handleInputChange('monthlyPrice', e?.target?.value)}
              placeholder="2999"
              required
              error={errors?.monthlyPrice}
            />
            <Input
              label="Yearly Price (₹)"
              type="number"
              value={formData?.yearlyPrice}
              onChange={(e) => handleInputChange('yearlyPrice', e?.target?.value)}
              placeholder="29990"
              required
              error={errors?.yearlyPrice}
            />
            <Input
              label="Trial Days"
              type="number"
              value={formData?.trialDays}
              onChange={(e) => handleInputChange('trialDays', e?.target?.value)}
              placeholder="14"
              required
              error={errors?.trialDays}
            />
          </div>
        </div>

        {/* Limits */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Gauge" size={20} className="text-primary" />
            Usage Limits
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Max Reports Limit"
              type="number"
              value={formData?.maxReportsLimit}
              onChange={(e) => handleInputChange('maxReportsLimit', e?.target?.value)}
              placeholder="50"
              description="-1 for unlimited"
              required
              error={errors?.maxReportsLimit}
            />
            <Input
              label="AI Usage Limit"
              type="number"
              value={formData?.aiUsageLimit}
              onChange={(e) => handleInputChange('aiUsageLimit', e?.target?.value)}
              placeholder="100"
              description="-1 for unlimited"
              required
              error={errors?.aiUsageLimit}
            />
            <Input
              label="Team Member Limit"
              type="number"
              value={formData?.teamMemberLimit}
              onChange={(e) => handleInputChange('teamMemberLimit', e?.target?.value)}
              placeholder="5"
              required
              error={errors?.teamMemberLimit}
            />
          </div>
        </div>

        {/* Feature Access */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Sparkles" size={20} className="text-primary" />
            Feature Access
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              checked={formData?.features?.bmcAccess}
              onChange={() => handleFeatureToggle('bmcAccess')}
              label="BMC Access"
              description="Business Model Canvas module"
            />
            <Checkbox
              checked={formData?.features?.predictiveAnalysis}
              onChange={() => handleFeatureToggle('predictiveAnalysis')}
              label="Predictive Analysis Access"
              description="Revenue forecasting and predictions"
            />
            <Checkbox
              checked={formData?.features?.goalIntelligence}
              onChange={() => handleFeatureToggle('goalIntelligence')}
              label="Goal Intelligence Access"
              description="Goal setting and tracking"
            />
            <Checkbox
              checked={formData?.features?.exportReports}
              onChange={() => handleFeatureToggle('exportReports')}
              label="Export Reports"
              description="Download reports in multiple formats"
            />
            <Checkbox
              checked={formData?.features?.prioritySupport}
              onChange={() => handleFeatureToggle('prioritySupport')}
              label="Priority Support"
              description="24/7 priority customer support"
            />
          </div>
        </div>

        {/* Plan Status */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="ToggleLeft" size={20} className="text-primary" />
            Plan Status
          </h3>
          <Checkbox
            checked={formData?.isActive}
            onChange={() => handleInputChange('isActive', !formData?.isActive)}
            label="Active"
            description="Make this plan available to users immediately"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={isSubmitting}
            iconName="Save"
            iconPosition="left"
            className="sm:flex-1"
          >
            Save Plan
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleCancel}
            disabled={isSubmitting}
            iconName="X"
            iconPosition="left"
            className="sm:flex-1"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreatePlanTab;